"use client";

import { useCallback, useState } from "react";
import type { ImdbPreviewNominee } from "../types";

export type ImdbPreviewJobStatus = "pending" | "completed" | "failed" | null;

export interface ImdbPreviewStatusResponse {
  status: string | null;
  nominees?: ImdbPreviewNominee[];
  error?: string;
}

export interface UseImdbPreviewStatusResult {
  status: ImdbPreviewJobStatus;
  nominees: ImdbPreviewNominee[] | null;
  error: string | null;
  loading: boolean;
  refetch: () => Promise<void>;
  reset: () => void;
}

/**
 * Fetches IMDB preview status from the API.
 * Used when the Fetch IMDB IDs modal is open so the client can poll or read current status.
 * @param awardShowId - Award show ID
 * @param apiBaseUrl - Optional base URL for API (default '' for same-origin in Next.js; set for mobile/other hosts)
 */
export function useImdbPreviewStatus(
  awardShowId: string,
  apiBaseUrl: string = ""
): UseImdbPreviewStatusResult {
  const [status, setStatus] = useState<ImdbPreviewJobStatus>(null);
  const [nominees, setNominees] = useState<ImdbPreviewNominee[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const reset = useCallback(() => {
    setStatus(null);
    setNominees(null);
    setError(null);
  }, []);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${apiBaseUrl}/api/admin/imdb-preview-status?awardShowId=${encodeURIComponent(awardShowId)}`;
      const res = await fetch(url);
      if (!res.ok) {
        setError("Failed to load status");
        setStatus(null);
        setNominees(null);
        return;
      }
      const data = (await res.json()) as ImdbPreviewStatusResponse;
      setStatus(data.status as ImdbPreviewJobStatus);
      if (data.status === "completed" && data.nominees?.length) {
        setNominees(data.nominees);
      } else {
        setNominees(null);
      }
      if (data.status === "failed" && data.error) {
        setError(data.error);
      }
    } finally {
      setLoading(false);
    }
  }, [awardShowId, apiBaseUrl]);

  return { status, nominees, error, loading, refetch, reset };
}
